import React from "react";
import { Switch } from "@/components/ui/switch";
import {
  SortableServiceList,
  SortableServiceItem,
} from "@/components/common/SortableServiceList";
import { HotelService, HotelServiceProviderType } from "@/types/hotelService";
import { CheckinSettingsResponse } from "@/types/booking-checkin-settings";

const Label = ({ children, className }: any) => (
  <label className={`text-2xl font-medium ${className || ""}`}>
    {children}
  </label>
);

interface FinalTabProps {
  settings: CheckinSettingsResponse | null;
  setSettings: React.Dispatch<
    React.SetStateAction<CheckinSettingsResponse | null>
  >;
  recommendedServices: SortableServiceItem[];
  setRecommendedServices: (services: SortableServiceItem[]) => void;
  featuredServices: SortableServiceItem[];
  setFeaturedServices: (services: SortableServiceItem[]) => void;
  allServices: HotelService[];
}

export const FinalTab = ({
  settings,
  setSettings,
  recommendedServices,
  setRecommendedServices,
  featuredServices,
  setFeaturedServices,
  allServices,
}: FinalTabProps) => {
  const updateSetting = (
    key: keyof CheckinSettingsResponse,
    value: CheckinSettingsResponse[keyof CheckinSettingsResponse],
  ) => {
    setSettings((previousSettings) =>
      previousSettings
        ? {
            ...previousSettings,
            [key]: value,
          }
        : previousSettings,
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Final Step Settings</h3>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={settings?.isShowRecommendedServicesOnCompleted}
            onCheckedChange={(checked) =>
              updateSetting("isShowRecommendedServicesOnCompleted", checked)
            }
          />
          <Label>Recommeded Services</Label>
        </div>
        {settings?.isShowRecommendedServicesOnCompleted && (
          <div className="ml-6 max-w-xl">
            <SortableServiceList
              items={recommendedServices}
              onReorder={setRecommendedServices}
              onRemove={(id) =>
                setRecommendedServices(
                  recommendedServices.filter((s) => s.id !== id),
                )
              }
              onAdd={(serviceId) => {
                const service = allServices.find((s) => s.id === serviceId);
                if (service) {
                  setRecommendedServices([
                    ...recommendedServices,
                    {
                      id: service.id,
                      serviceId: service.id,
                      name: service.name,
                    },
                  ]);
                }
              }}
              availableServices={allServices.filter(
                (s) =>
                  s.providerType === HotelServiceProviderType.GUEST_SERVICE,
              )}
              placeholder="Add recommended service"
            />
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={settings?.isShowFeaturedActivitiesOnCompleted}
            onCheckedChange={(checked) =>
              updateSetting("isShowFeaturedActivitiesOnCompleted", checked)
            }
          />
          <Label>Featured Activities</Label>
        </div>
        {settings?.isShowFeaturedActivitiesOnCompleted && (
          <div className="ml-6 max-w-xl">
            <SortableServiceList
              items={featuredServices}
              onReorder={setFeaturedServices}
              onRemove={(id) =>
                setFeaturedServices(featuredServices.filter((s) => s.id !== id))
              }
              onAdd={(serviceId) => {
                const service = allServices.find((s) => s.id === serviceId);
                if (service) {
                  setFeaturedServices([
                    ...featuredServices,
                    {
                      id: service.id,
                      serviceId: service.id,
                      name: service.name,
                    },
                  ]);
                }
              }}
              availableServices={allServices.filter(
                (s) =>
                  s.providerType === HotelServiceProviderType.EXTERNAL_SERVICE,
              )}
              placeholder="Add featured activity"
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={settings?.isShowFAQOnCompleted}
          onCheckedChange={(checked) =>
            updateSetting("isShowFAQOnCompleted", checked)
          }
        />
        <Label>Show FAQ on Completed</Label>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={settings?.isShowTaCOnCompleted}
          onCheckedChange={(checked) =>
            updateSetting("isShowTaCOnCompleted", checked)
          }
        />
        <Label>Show Terms & Conditions on Completed</Label>
      </div>
    </div>
  );
};
